import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Text,
  Button,
  DialogContent,
  Dialog,
  theme,
} from "@aksara-ui/react";
import styled, { keyframes } from "styled-components";
import ApiService from "service/Api";
import RaffleAncolService from "service/RaffleAncol";
import moment from "moment";

const Shell = styled(Box)`
  width: 100%;
  max-width: 1560px;
  margin: 0 auto;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Header = styled(Box)`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-shrink: 0;
`;

const scrollbarThin = `
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 999px;
  }
`;

const Wrapper = styled(Box)`
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) minmax(320px, 0.95fr);
  gap: 18px;
  height: 100%;
  min-height: 0;
  align-items: start;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    grid-auto-rows: auto;
    height: auto;
    overflow-y: auto;
    ${scrollbarThin}
  }
`;

const Stage = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 18px;
  background: linear-gradient(180deg, #eef5ff 0%, #ffffff 100%);
  border-radius: 28px;
  padding: 22px;
  box-shadow: 0 22px 80px rgba(12, 194, 185, 0.14);
  min-height: 0;
  height: 100%;

  @media (max-width: 960px) {
    height: auto;
  }

  @media (max-width: 560px) {
    padding: 18px;
    gap: 14px;
  }
`;

const PrizeCard = styled(Box)`
  display: flex;
  align-items: stretch;
  padding: 0;
  border-radius: 24px;
  overflow: hidden;
  background: linear-gradient(180deg, #ffffff 0%, #f1f7ff 100%);
  box-shadow: inset 0 0 0 1px rgba(36, 114, 255, 0.08);
`;

const PrizeMedia = styled(Box)`
  position: relative;
  width: 100%;
  min-height: 440px;
  overflow: hidden;
  background: linear-gradient(135deg, #dce9ff, #f8fbff);

  @media (max-width: 760px) {
    min-height: 340px;
  }
`;

const PrizeImage = styled(Box)`
  width: 100%;
  height: 100%;
  min-height: 440px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  @media (max-width: 760px) {
    min-height: 340px;
  }
`;

const PrizeOverlay = styled(Box)`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: flex-end;
  padding: 20px;
  pointer-events: none;
`;

const PrizeImagePlaceholder = styled(Box)`
  width: 100%;
  min-height: 440px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: repeating-linear-gradient(
    135deg,
    #eef5ff,
    #eef5ff 12px,
    #f6f9ff 12px,
    #f6f9ff 24px
  );
  color: ${theme.colors.grey06};
  font-size: 48px;

  @media (max-width: 760px) {
    min-height: 300px;
  }
`;

const NumberCard = styled(Box)`
  border-radius: 24px;
  padding: 20px;
  background: #ffffff;
  box-shadow: 0 24px 80px rgba(22, 73, 165, 0.08);
  text-align: center;
`;

const DigitBox = styled(Box)`
  width: 64px;
  height: 84px;
  border-radius: 14px;
  border: 2px solid #1547fa;
  font-size: 36px;
  font-weight: 900;
  color: #070707;
  background: #ecedee;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const spinPulse = keyframes`
  0%, 100% { opacity: 0.55; transform: scale(0.99); }
  50% { opacity: 1; transform: scale(1); }
`;

const DrawButton = styled(Button)<{ isDrawing?: boolean }>`
  height: 60px;
  min-width: 280px;
  font-size: 18px;
  font-weight: 900;
  transition: transform 0.15s ease;
  animation: ${({ isDrawing }) => (isDrawing ? spinPulse : "none")} 0.5s ease-in-out infinite;

  &:not(:disabled):hover {
    transform: translateY(-2px);
  }

  @media (max-width: 560px) {
    width: 100%;
    min-width: 0;
    height: 60px;
    font-size: 17px;
  }
`;

const progressGrow = keyframes`
  from { transform: scaleX(0); }
`;

const ProgressTrack = styled(Box)`
  height: 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  overflow: hidden;
`;

const ProgressFill = styled(Box)<{ percent: number }>`
  height: 100%;
  width: ${({ percent }) => percent}%;
  border-radius: 999px;
  background: linear-gradient(90deg, #34d399, #fbbf24);
  transform-origin: left;
  transition: width 0.25s ease-out;
  animation: ${progressGrow} 0.3s ease-out;
`;

const confettiFall = keyframes`
  0% { transform: translateY(-20px) rotate(0deg); opacity: 1; }
  100% { transform: translateY(110vh) rotate(540deg); opacity: 0; }
`;

const ConfettiLayer = styled(Box)`
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 9999;
`;

const ConfettiPiece = styled(Box)<{
  delay: number;
  left: number;
  size: number;
  hue: number;
}>`
  position: absolute;
  top: -20px;
  left: ${({ left }) => left}%;
  width: ${({ size }) => size}px;
  height: ${({ size }) => Math.max(6, Math.round(size * 0.4))}px;
  border-radius: 4px;
  background: ${({ hue }) => `hsl(${hue}, 90%, 65%)`};
  opacity: 0;
  filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.18));
  animation: ${confettiFall} 2.5s ease-out forwards;
  animation-delay: ${({ delay }) => delay}s;
`;

const WinnerPanel = styled(Box)`
  border-radius: 24px;
  padding: 22px;
  background: linear-gradient(180deg, #0d2f82 0%, #092259 100%);
  color: #ffffff;
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;

  @media (max-width: 960px) {
    height: auto;
  }

  @media (max-width: 560px) {
    padding: 22px;
  }
`;

const WinnerList = styled(Box)`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-right: 6px;
  ${scrollbarThin}

  @media (max-width: 960px) {
    flex: none;
    height: 300px;
  }
`;

const winnerIn = keyframes`
  from { opacity: 0; transform: translateY(10px) scale(0.97); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const DrawBanner = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 18px 20px;
  border-radius: 24px;
  background: linear-gradient(120deg, #0d2f82 0%, #2472ff 100%);
  box-shadow: 0 18px 50px rgba(13, 54, 161, 0.28);

  @media (max-width: 560px) {
    padding: 20px;
  }
`;

const Footer = styled(Box)`
  display: flex;
  justify-content: flex-end;
  padding-top: 12px;
  margin-top: 6px;
  border-top: 1px solid ${theme.colors.grey03};
`;

const WinnerStack = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const WinnerCard = styled(Box)<{ isNew?: boolean }>`
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 16px;
  background: ${({ isNew }) =>
    isNew
      ? "linear-gradient(135deg, rgba(251,191,36,0.28), rgba(255,255,255,0.08))"
      : "rgba(255,255,255,0.08)"};
  box-shadow: ${({ isNew }) => (isNew ? "0 0 0 1px rgba(251,191,36,0.55)" : "none")};
  animation: ${winnerIn} 0.3s ease-out both;

  p {
    min-width: 0;
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const DisqualifyButton = styled(Button)`
  flex-shrink: 0;
  min-width: 120px;
  height: 40px;
  font-size: 13px;
`;

/* ============= WINNER ANNOUNCEMENT POPUP ============= */
/* NOTE: this popup is only ever shown for SINGLE draw mode. */

const WinnerPopupOverlay = styled(Box)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(4, 12, 36, 0.82);
  backdrop-filter: blur(6px);
  padding: 32px;
  box-sizing: border-box;
`;

const winnerPopIn = keyframes`
  0% { opacity: 0; transform: scale(0.8) translateY(30px); }
  60% { opacity: 1; transform: scale(1.04) translateY(-6px); }
  100% { opacity: 1; transform: scale(1) translateY(0); }
`;

const WinnerPopupCard = styled(Box)`
  width: 100%;
  max-width: 720px;
  background: linear-gradient(180deg, #ffffff 0%, #f3f7ff 100%);
  border-radius: 36px;
  padding: 56px 56px 44px;
  box-shadow: 0 40px 120px rgba(9, 34, 89, 0.5);
  text-align: center;
  animation: ${winnerPopIn} 0.55s cubic-bezier(0.18, 0.89, 0.32, 1.28) both;
  box-sizing: border-box;

  @media (max-width: 760px) {
    padding: 36px 24px 28px;
    border-radius: 28px;
  }
`;

const WinnerPopupBadge = styled(Box)`
  display: inline-block;
  padding: 10px 26px;
  border-radius: 999px;
  background: linear-gradient(90deg, #fbbf24, #f59e0b);
  color: #3b1d00;
  font-weight: 900;
  font-size: 16px;
  letter-spacing: 2px;
  margin-bottom: 26px;
`;

const WinnerPopupName = styled(Box)`
  font-size: 56px;
  font-weight: 900;
  color: #0d2f82;
  line-height: 1.15;
  margin-bottom: 14px;
  word-break: break-word;

  @media (max-width: 760px) {
    font-size: 38px;
  }
`;

const WinnerPopupPhone = styled(Box)`
  font-size: 24px;
  font-weight: 700;
  color: #4b5566;
  letter-spacing: 3px;
  margin-bottom: 34px;

  @media (max-width: 760px) {
    font-size: 19px;
  }
`;



const WinnerPopupCloseButton = styled(Button)`
  margin-top: 32px;
  min-width: 260px;
  height: 54px;
  font-size: 17px;
  font-weight: 800;
`;

/* ================= TYPES ================= */

interface RaffleParticipant {
  id: number | string;
  name: string;
  phone: string;
  code?: string;
}

interface RaffleAncolViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  raffle: any;
  appUrl: string;
  /** Optional real participant pool; falls back to generated sample data when omitted. */
  participantPool?: RaffleParticipant[];
  /** Number of winners to draw. Defaults to 12, capped by pool size. */
  winnerCount?: number;
}

const SPIN_TICK_MS = 60;
const CONFETTI_DURATION_MS = 1600;
/** How long the digits keep "rolling" before auto-finalizing in multi-draw mode. */
const AUTO_DRAW_SPIN_MS = 900;
/** Pause before the next automatic draw starts in multi-draw mode (no popup to wait on). */
const AUTO_DRAW_NEXT_DELAY_MS = 700;

const generateDummyParticipants = (count: number): RaffleParticipant[] =>
  Array.from({ length: count }, (_, i) => ({
    id: i + 1,
    name: `Peserta ${i + 1}`,
    phone: `08${Math.floor(100000000 + Math.random() * 899999999)}`,
  }));

/** Unbiased Fisher–Yates shuffle (Array.sort with Math.random is not uniformly random). */
function shuffle<T>(input: T[]): T[] {
  const arr = [...input];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

/** Masks a phone number, keeping everything but the last 4 digits which become "****". */
const maskPhone = (phone?: string | null): string => {
  if (!phone) return "";
  return phone.length > 4 ? `${phone.slice(0, -4)}****` : phone;
};

/* ================= COMPONENT ================= */

const RaffleAncolViewModal: React.FC<RaffleAncolViewModalProps> = ({
  isOpen,
  onClose,
  raffle,
  appUrl,
  participantPool,
  winnerCount,
}) => {
  const [isDrawing, setIsDrawing] = useState(false);
  const [digits, setDigits] = useState<string[]>(Array(9).fill("0"));
  const [winners, setWinners] = useState<RaffleParticipant[]>([]);
  const [showConfetti, setShowConfetti] = useState(false);
  const [loadingApi, setLoadingApi] = useState(false);
  const [isInitialDataLoading, setIsInitialDataLoading] = useState(true);
  const [disqualifyingId, setDisqualifyingId] = useState<number | string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Winner announcement popup (confetti + name + phone + prize banner).
  // Only ever used in SINGLE draw mode — multiple draw skips the popup entirely
  // and drops each winner straight into the winner list.
  const [showWinnerPopup, setShowWinnerPopup] = useState(false);
  const [currentWinnerPopup, setCurrentWinnerPopup] = useState<RaffleParticipant | null>(null);

  const spinIntervalRef = useRef<number | null>(null);
  const confettiTimeoutRef = useRef<number | null>(null);
  const drawTimerRef = useRef<number | null>(null);
  const autoNextTimeoutRef = useRef<number | null>(null);
  const isDrawingRef = useRef(false);
  const pendingWinnerDataRef = useRef<any>(null);
  const winnersCountRef = useRef(0);

  const fullPool = useMemo<RaffleParticipant[]>(
    () => participantPool ?? generateDummyParticipants(120),
    [participantPool],
  );

  const totalWinner = winnerCount ?? raffle?.qty ?? 0;

  const targetWinnerCount = Math.min(
    totalWinner,
    fullPool.length
  );

  // Freeze the drawing order once per raffle open so a re-render mid-draw
  // never reshuffles the pool out from under an in-progress draw.
  const drawOrder = useMemo<RaffleParticipant[]>(
    () => shuffle(fullPool).slice(0, targetWinnerCount),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [raffle?.id, isOpen, fullPool, targetWinnerCount],
  );

  // "1" = draw every winner automatically based on qty, "0" = draw one winner at a time.
  const isMultipleDraw = String(raffle?.is_multiple_draw ?? "0") === "1";

  // Keep a ref in sync with the winners list so timer callbacks always see
  // the latest count without depending on a stale closure.
  useEffect(() => {
    winnersCountRef.current = winners.length;
  }, [winners]);

  const randomRollingDigits = () =>
    Array.from({ length: 9 }, () =>
      Math.floor(Math.random() * 10).toString()
    );

  const normalizeWinner = (item: any): RaffleParticipant => ({
    id: item?.id ?? item?.coupon_code ?? item?.code ?? item?.winner_id ?? `${item?.name ?? "winner"}-${Math.random()}`,
    name: item?.name ?? item?.user_name ?? item?.fullname ?? item?.winner_name ?? "Pemenang Tidak Diketahui",
    phone: item?.phone ?? item?.phone_number ?? item?.coupon_code ?? item?.code ?? "-",
    code: item?.coupon_code ?? item?.code ?? item?.winner_code ?? "",
  });

  const loadWinnersFromApi = async () => {
    if (!raffle?.id) {
      setIsInitialDataLoading(false);
      return;
    }

    setIsInitialDataLoading(true);

    try {
      const apiWinners = await RaffleAncolService.getGrandPrizeWinners(
        raffle.id.toString()
      );
      const normalized = (apiWinners || []).map(normalizeWinner);
      setWinners(normalized);
    } catch (error) {
      setWinners([]);
    } finally {
      setIsInitialDataLoading(false);
    }
  };

  const clearAllTimers = () => {
    if (spinIntervalRef.current) {
      window.clearInterval(spinIntervalRef.current);
      spinIntervalRef.current = null;
    }
    if (drawTimerRef.current) {
      window.clearTimeout(drawTimerRef.current);
      drawTimerRef.current = null;
    }
    if (confettiTimeoutRef.current) {
      window.clearTimeout(confettiTimeoutRef.current);
      confettiTimeoutRef.current = null;
    }
    if (autoNextTimeoutRef.current) {
      window.clearTimeout(autoNextTimeoutRef.current);
      autoNextTimeoutRef.current = null;
    }
  };
  

  // Reset state whenever the modal closes, and always clean up timers on unmount.
  useEffect(() => {
    if (!isOpen) {
      setDigits(Array(9).fill("0"));
      setIsDrawing(false);
      setShowConfetti(false);
      setLoadingApi(false);
      setIsInitialDataLoading(false);
      setIsFullscreen(false);
      setShowWinnerPopup(false);
      setCurrentWinnerPopup(null);
      isDrawingRef.current = false;
      pendingWinnerDataRef.current = null;
      clearAllTimers();
      return;
    }

    loadWinnersFromApi();
    return clearAllTimers;
  }, [isOpen, raffle?.id]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  if (!raffle) return null;

  const imageUrl = raffle.img_url
    ? raffle.img_url.startsWith("http")
      ? raffle.img_url
      : `${appUrl}/storage/${raffle.img_url}`
    : undefined;

  const allDrawn = drawOrder.length > 0 && winners.length >= drawOrder.length;

  const finalizeWinner = (data: any = null) => {
    if (spinIntervalRef.current) {
      window.clearInterval(spinIntervalRef.current);
      spinIntervalRef.current = null;
    }

    const fallbackParticipant = drawOrder[winnersCountRef.current] as RaffleParticipant | undefined;
    const code = data?.coupon_code || data?.code || fallbackParticipant?.code || "000000000";
    const name = data?.username || data?.fullname || data?.name || fallbackParticipant?.name || "Pemenang Tidak Diketahui";
    const phone = data?.phone || data?.phone_number || fallbackParticipant?.phone || code;
    const codeDigits = code.replace(/^GBYR-/, "").split("");

    setDigits(codeDigits.length === 9 ? codeDigits : Array(9).fill("0"));

    const winnerEntry: RaffleParticipant = {
      id: data?.id ?? code,
      name,
      phone,
      code,
    };

    const nextCount = winnersCountRef.current + 1;
    winnersCountRef.current = nextCount;
    setWinners((prev) => [winnerEntry, ...prev]);

    setShowConfetti(true);
    confettiTimeoutRef.current = window.setTimeout(() => {
      setShowConfetti(false);
      confettiTimeoutRef.current = null;
    }, CONFETTI_DURATION_MS);

    setIsDrawing(false);
    setLoadingApi(false);
    isDrawingRef.current = false;
    pendingWinnerDataRef.current = null;

    if (isMultipleDraw) {
      // Multiple draw: no popup at all — the winner is already in the list.
      // Automatically move on to the next draw until everyone has been drawn.
      const stillMore = nextCount < drawOrder.length;
      if (stillMore) {
        autoNextTimeoutRef.current = window.setTimeout(() => {
          autoNextTimeoutRef.current = null;
          void startDrawing();
        }, AUTO_DRAW_NEXT_DELAY_MS);
      }
    } else {
      // Single draw: show the dedicated winner announcement popup.
      setCurrentWinnerPopup(winnerEntry);
      setShowWinnerPopup(true);
    }
  };

  const startDrawing = async () => {
    if (isDrawingRef.current || loadingApi || allDrawn || drawOrder.length === 0) return;

    setIsDrawing(true);
    setLoadingApi(true);
    isDrawingRef.current = true;
    pendingWinnerDataRef.current = null;
    clearAllTimers();

    spinIntervalRef.current = window.setInterval(() => {
      setDigits(randomRollingDigits());
    }, SPIN_TICK_MS);

    try {
      const res = await ApiService.apiRequest({
        method: "post",
        url: "/admin/raffle-ancol/draw/grand-prize",
        data: { catalog_id: raffle?.id?.toString?.() ?? "" },
      });

      if (!isDrawingRef.current) return;

      pendingWinnerDataRef.current = res.data;
      setLoadingApi(false);

      if (isMultipleDraw) {
        // Multiple draw: no second click needed — let the digits roll briefly
        // for effect, then reveal the winner automatically (straight into the list).
        drawTimerRef.current = window.setTimeout(() => {
          finalizeWinner(pendingWinnerDataRef.current);
          drawTimerRef.current = null;
        }, AUTO_DRAW_SPIN_MS);
      }
    } catch (error) {
      if (!isDrawingRef.current) return;
      pendingWinnerDataRef.current = null;
      alert("Undian gagal. Coba lagi.");
      setIsDrawing(false);
      setLoadingApi(false);
      isDrawingRef.current = false;
      if (spinIntervalRef.current) {
        window.clearInterval(spinIntervalRef.current);
        spinIntervalRef.current = null;
      }
    }
  };

  const drawNextWinner = () => {
    if (allDrawn || drawOrder.length === 0) return;

    if (isMultipleDraw) {
      // Single click kicks off the fully automatic sequence for every remaining
      // winner; each one lands directly in the list with no popup interruption.
      if (isDrawingRef.current) return;
      void startDrawing();
      return;
    }

    // Single draw mode: first click starts the roulette, second click reveals the winner in a popup.
    if (isDrawingRef.current) {
      finalizeWinner(pendingWinnerDataRef.current);
      return;
    }

    void startDrawing();
  };

  const handleClosePopup = () => {
    // Only relevant for single draw mode — just dismiss the popup.
    setShowWinnerPopup(false);
    setCurrentWinnerPopup(null);
  };

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
        setIsFullscreen(true);
        return;
      }

      await document.exitFullscreen();
      setIsFullscreen(false);
    } catch (error) {
      setIsFullscreen(Boolean(document.fullscreenElement));
    }
  };

  const handleClose = () => {
    clearAllTimers();
    setDigits(Array(9).fill("0"));
    setIsDrawing(false);
    setShowConfetti(false);
    setLoadingApi(false);
    setIsFullscreen(false);
    setShowWinnerPopup(false);
    setCurrentWinnerPopup(null);

    if (document.fullscreenElement) {
      void document.exitFullscreen();
    }

    onClose();
  };

  const handleDisqualifyWinner = async (winner: RaffleParticipant) => {
    if (!raffle?.id || disqualifyingId === winner.id) return;

    setDisqualifyingId(winner.id);

    try {
      await RaffleAncolService.deleteGrandPrizeWinner(
        raffle.id.toString(),
        winner.id.toString()
      );
    } catch (error) {
      // keep the UI responsive even when the API rejects the request
    } finally {
      setWinners((prev) => prev.filter((item) => item.id !== winner.id));
      winnersCountRef.current = Math.max(0, winnersCountRef.current - 1);
      setDisqualifyingId(null);
    }
  };

  const progressPercent = drawOrder.length
    ? Math.round((winners.length / drawOrder.length) * 100)
    : 0;

  const isDrawButtonDisabled =
    allDrawn ||
    drawOrder.length === 0 ||
    isInitialDataLoading ||
    (isMultipleDraw ? isDrawing : isDrawing && loadingApi);

  const drawButtonLabel = isInitialDataLoading
    ? "Memuat..."
    : allDrawn
    ? "Semua Pemenang Terpilih 🎉"
    : isMultipleDraw
    ? isDrawing
      ? "Sedang Mengundi..."
      : "Mulai Undian"
    : isDrawing
    ? "Berhenti"
    : "Mulai Undian";

  const drawBannerHint = allDrawn
    ? "Undian selesai"
    : isMultipleDraw
    ? "Semua pemenang akan diundi otomatis satu per satu dan langsung masuk ke daftar"
    : isDrawing
    ? "Klik tombol untuk menghentikan dan menampilkan pemenang"
    : "Klik mulai, lalu klik lagi untuk menghentikan";

  return (
    <>
      <Dialog
        isOpen={isOpen}
        onClose={handleClose}
        width="100vw"
        maxWidth="100vw"
        height="100vh"
        maxHeight="100vh"
        my={0}
        mx={0}
        borderRadius={0}
        style={{ margin: 0 }}
      >
        <DialogContent
          style={{
            padding: 0,
            minWidth: 0,
            height: "100%",
            maxHeight: "100%",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            boxSizing: "border-box",
          }}
        >
          <Box
            padding={24}
            style={{
              flex: 1,
              minHeight: 0,
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Shell>
              <Header mb={24}>
                <Box>
                  <Text scale={700} fontWeight={900}>
                    Pengundian Hadiah Utama
                  </Text>
                </Box>
              </Header>

              <Wrapper style={{ flex: 1, minHeight: 0 }}>
                <Stage>
                  <PrizeCard>
                    <PrizeMedia>
                      {imageUrl ? (
                        <PrizeImage>
                          <img src={imageUrl} alt={raffle.reward_name} loading="lazy" />
                        </PrizeImage>
                      ) : (
                        <PrizeImagePlaceholder aria-hidden="true">🎁</PrizeImagePlaceholder>
                      )}
                      <PrizeOverlay>
                        <Box style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                          <Text scale={600} fontWeight={900} color="white">
                            {raffle.reward_name}
                          </Text>
                          <Text scale={300} color="rgba(255,255,255,0.92)">
                            {raffle.description
                              ? (() => {
                                  const plain = raffle.description.replace(/<[^>]+>/g, "");
                                  return plain.length > 120
                                    ? `${plain.substring(0, 120)}…`
                                    : plain;
                                })()
                              : "Deskripsi tidak tersedia."}
                          </Text>
                          <Text scale={300} color="rgba(255,255,255,0.9)" fontWeight={700} mt={2}>
                            Periode: {moment(raffle.active_periode_start).format("DD MMM YYYY")} –{" "}
                            {moment(raffle.active_periode_end).format("DD MMM YYYY")}
                          </Text>
                        </Box>
                      </PrizeOverlay>
                    </PrizeMedia>
                  </PrizeCard>
                  <NumberCard>
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    style={{ gap: "12px" }}
                  >
                    <Text fontSize="30px" fontWeight={900} color="black">
                      GBYR -
                    </Text>

                    <Box display="flex">
                      {digits.map((d, i) => (
                        <DigitBox key={i}>{d}</DigitBox>
                      ))}
                    </Box>
                  </Box>
                  </NumberCard>

                  <DrawBanner>
                    <Box>
                      <Text scale={300} color="rgba(255,255,255,0.75)" fontWeight={700}>
                        {drawBannerHint}
                      </Text>
                    </Box>
                    <DrawButton
                      variant="primary"
                      onClick={drawNextWinner}
                      disabled={isDrawButtonDisabled}
                      isDrawing={isDrawing}
                    >
                      {drawButtonLabel}
                    </DrawButton>
                  </DrawBanner>

                </Stage>

              <WinnerPanel>
                <Box mb={16}>
                  <Text fontSize={26} fontWeight={900}>
                    Pemenang Terpilih
                  </Text>
                </Box>

                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={8}
                >
                  <Text scale={300} color="rgba(255,255,255,0.75)" fontWeight={700}>
                    Progres Undian
                  </Text>
                  <Text scale={300} color="rgba(255,255,255,0.95)" fontWeight={900}>
                    {winners.length}/{drawOrder.length}
                  </Text>
                </Box>

                <ProgressTrack mb={20}>
                  <ProgressFill percent={progressPercent} />
                </ProgressTrack>

                <WinnerList aria-live="polite">
                  {winners.length === 0 ? (
                    <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                      <Text scale={300} color="rgba(255,255,255,0.65)">
                        {drawOrder.length === 0
                          ? "Belum ada peserta yang bisa diundi."
                          : isMultipleDraw
                          ? "Klik tombol undian untuk mengundi semua pemenang otomatis."
                          : "Klik tombol undian untuk memilih pemenang satu per satu."}
                      </Text>
                    </Box>
                  ) : (
                    <WinnerStack>
                      {winners.map((winner, index) => (
                        <WinnerCard key={winner.id} isNew={index === 0}>
                          <Box minWidth={0}>
                            <Text
                              scale={500}
                              fontWeight={700}
                              style={{
                                letterSpacing: "5px",
                                display: "block",
                                marginBottom: 4,
                                color:"#fff8af"
                              }}
                            >
                              {winner.code || winner.phone}
                            </Text>

                            <Text
                              scale={400}
                              style={{ display: "block" }}
                            >
                              {winner.name}
                              {winner.phone ? ` (${maskPhone(winner.phone)})` : ""}
                            </Text>
                          </Box>
                          <DisqualifyButton
                            variant="default"
                            onClick={() => handleDisqualifyWinner(winner)}
                            disabled={disqualifyingId === winner.id}
                          >
                            {disqualifyingId === winner.id ? "Memproses..." : "X"}
                          </DisqualifyButton>
                        </WinnerCard>
                      ))}
                    </WinnerStack>
                  )}
                </WinnerList>
              </WinnerPanel>
              </Wrapper>

              <Footer>
                <Box display="flex" style={{ gap: 12 }}>
                  <Button variant="default" onClick={toggleFullscreen}>
                    {isFullscreen ? "Keluar Full Screen" : "Full Screen"}
                  </Button>
                </Box>
              </Footer>
            </Shell>
          </Box>
        </DialogContent>
      </Dialog>

      {showConfetti && (
        <ConfettiLayer aria-hidden="true">
          {Array.from({ length: 32 }).map((_, i) => (
            <ConfettiPiece
              key={i}
              delay={Math.random() * 1.2}
              left={Math.random() * 100}
              size={Math.floor(8 + Math.random() * 16)}
              hue={Math.floor(Math.random() * 360)}
            />
          ))}
        </ConfettiLayer>
      )}

      {/* Winner announcement popup — SINGLE draw mode only. Multiple draw never sets
          showWinnerPopup/currentWinnerPopup, so this block simply won't render for it. */}
      {!isMultipleDraw && showWinnerPopup && currentWinnerPopup && (
      <WinnerPopupOverlay>
        <WinnerPopupCard
          style={{
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* ================= CONFETTI ================= */}
          <div
            style={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              overflow: "hidden",
              borderRadius: 32,
              zIndex: 1,
            }}
          >
            {Array.from({ length: 50 }).map((_, i) => {
              const left = Math.random() * 100;
              const delay = Math.random() * 2;
              const duration = 2 + Math.random() * 2;
              const size = 8 + Math.random() * 10;

              const colors = [
                "#FFD700",
                "#FF3D00",
                "#00E5FF",
                "#7C4DFF",
                "#69F0AE",
                "#FF4081",
                "#FF9800",
              ];

              return (
                <div
                  key={i}
                  style={{
                    position: "absolute",
                    left: `${left}%`,
                    top: -20,
                    width: size,
                    height: size * 0.45,
                    background: colors[i % colors.length],
                    borderRadius: 3,
                    transform: `rotate(${Math.random() * 360}deg)`,
                    animation: `popupConfetti ${duration}s linear ${delay}s infinite`,
                    opacity: 0.95,
                  }}
                />
              );
            })}
          </div>

          <style>
            {`
              @keyframes popupConfetti{
                0%{
                  transform:translateY(-20px) rotate(0deg);
                  opacity:1;
                }
                100%{
                  transform:translateY(900px) rotate(720deg);
                  opacity:0;
                }
              }
            `}
          </style>

          {/* ===================================================== */}
          {/* Isi popup */}
          {/* ===================================================== */}

          <div style={{ position: "relative", zIndex: 2 }}>
            {/* Badge */}
            <WinnerPopupBadge
              style={{
                background: "linear-gradient(90deg,#ff9800,#FFD700,#ff9800)",
                color: "#7A1F00",
                padding: "12px 32px",
                borderRadius: 999,
                fontWeight: 900,
                fontSize: 24,
                boxShadow: "0 0 30px rgba(255,215,0,.6)",
              }}
            >
           🎉 SELAMAT KEPADA 🎉
            </WinnerPopupBadge>

              {/* Nama */}
              <WinnerPopupName
                style={{
                  fontSize: 60,
                  fontWeight: 900,
                  color: "#494747",
                  textAlign: "center",
                  textShadow: "0 0 18px rgba(255,215,0,.7)",
                  marginTop: 20,
                  letterSpacing: 2,
                }}
              >
                {currentWinnerPopup.name}
              </WinnerPopupName>

              <div
                style={{
                  width: "100%",
                  height: 2,
                  margin: "22px 0",
                  background:
                    "linear-gradient(to right,transparent,#FFD700,transparent)",
                }}
              />

              {/* Coupon Code */}
              <div
                style={{
                  fontSize: 38, // lebih besar dari phone
                  fontWeight: 800,
                  color: "#D97706",
                  letterSpacing: 3,
                  marginBottom: 40,
                  textAlign: "center",
                }}
              >
                {currentWinnerPopup.code}
              </div>

              {/* Phone */}
              <WinnerPopupPhone
                style={{
                  fontSize: 30,
                  fontWeight: 700,
                  color: "#242423",
                  letterSpacing: 2,
                }}
              >
                {(() => {
                  const phone =
                    currentWinnerPopup.phone &&
                    currentWinnerPopup.phone !== currentWinnerPopup.code
                      ? currentWinnerPopup.phone
                      : currentWinnerPopup.code;

                  return maskPhone(phone);
                })()}
              </WinnerPopupPhone>

            <div
              style={{
                width: "100%",
                height: 2,
                margin: "22px 0 30px",
                background:
                  "linear-gradient(to right,transparent,#FFD700,transparent)",
              }}
            />

            {/* Hadiah */}
            <div
              style={{
                width: "100%",
                padding: "18px 24px",
                borderRadius: 22,
                background:
                  "linear-gradient(135deg,#ff512f,#dd2476,#ff9800,#FFD700)",
                textAlign: "center",
                marginBottom: 24,
                boxShadow: "0 15px 35px rgba(255,120,0,.35)",
              }}
            >
              <div
                style={{
                  color: "#FFF9C4",
                  fontWeight: 700,
                  fontSize: 18,
                  letterSpacing: 2,
                  marginBottom: 8,
                }}
              >
                🎁 HADIAH
              </div>

              <div
                style={{
                  color: "#fff",
                  fontWeight: 900,
                  fontSize: 40,
                  lineHeight: 1.2,
                  textTransform: "uppercase",
                  textShadow: "0 3px 10px rgba(0,0,0,.4)",
                }}
              >
                {raffle.reward_name}
              </div>
            </div>

            {/* Gambar */}
            <div
              style={{
                width: 500,
                height: 260,
                margin: "0 auto",
                borderRadius: 28,
                overflow: "hidden",
                border: "6px solid #FFD700",
                background: "#fff",
                boxShadow:
                  "0 0 35px rgba(255,215,0,.8),0 15px 35px rgba(0,0,0,.35)",
              }}
            >
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={raffle.reward_name}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    fontSize: 120,
                  }}
                >
                  🎁
                </div>
              )}
            </div>

            {/* Button */}
            <WinnerPopupCloseButton
              variant="primary"
              onClick={handleClosePopup}
              style={{
                marginTop: 40,
                fontSize: 22,
                fontWeight: 900,
                padding: "14px 42px",
                background:
                  "linear-gradient(90deg,#ff9800,#FFD700,#ff9800)",
                color: "#7A1F00",
                border: "none",
                boxShadow: "0 8px 25px rgba(255,215,0,.5)",
              }}
            >
              Tutup
            </WinnerPopupCloseButton>
          </div>
        </WinnerPopupCard>
      </WinnerPopupOverlay>
      )}
    </>
  );
};

export default RaffleAncolViewModal;
